import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { compressMediaFiles } from '../utils/imageCompression';
import { normalizeVideoUrlsInText } from '../utils/videoProviders';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getMediaUrl } from '../utils/mediaUrl';
import GifPicker from './GifPicker';
import AudioRecorder from './AudioRecorder';
import AudioPlayer from './AudioPlayer';
import MentionSuggestions from './MentionSuggestions';
import BazzAIStudio from './BazzAIStudio';
import { CameraModal, EmojiPicker, ImageEditorModal } from './LazyModals';
import { useMobile } from '../utils/useMobile';

const REPLY_OPTIONS = [
  { value: 'everyone', labelKey: 'compose.replyEveryone', icon: 'fa-globe' },
  { value: 'following', labelKey: 'compose.replyFollowing', icon: 'fa-user-friends' },
  { value: 'mentioned', labelKey: 'compose.replyMentioned', icon: 'fa-at' },
];

const POLL_DURATION_KEYS = [
  { value: 24, labelKey: 'compose.poll1day' },
  { value: 72, labelKey: 'compose.poll3days' },
  { value: 168, labelKey: 'compose.poll7days' },
];

const MAX_CHARS = 280;

/**
 * CreatePost component — redesigned per premium mobile UX spec:
 *  - "Post" action lives in the header top-right (opposite the back arrow)
 *  - Toolbar icons reduced and recolored to brand indigo
 *  - Hard divider removed; textarea breathes full width
 *  - Privacy toggle moved into toolbar left side
 *  - Toolbar docks to keyboard on mobile
 */
const CreatePost = ({
  onPostCreated,
  initialContent = '',
  /** Photos / videos to start with (e.g. shared from another app) */
  initialFiles = null,
  communityId = null,
  quotedPost = null,
  /** When true the component renders its own sticky header with Post button.
   *  Pass false when embedded in a page that already has its own header. */
  showHeader = false,
  onBack,
}) => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const toast = useToast();
  const isMobile = useMobile();

  const userProfilePic = user?.profilePicture || user?.profile_picture;

  const [content, setContent] = useState(initialContent);
  const [whoCanReply, setWhoCanReply] = useState('everyone');
  const [showReplyDropdown, setShowReplyDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [media, setMedia] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [location, setLocation] = useState('');
  const [locationLat, setLocationLat] = useState(null);
  const [locationLon, setLocationLon] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showPoll, setShowPoll] = useState(false);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollDurationHours, setPollDurationHours] = useState(24);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [showAIStudio, setShowAIStudio] = useState(false);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const [showCamera, setShowCamera] = useState(false);

  React.useEffect(() => {
    if (user && !user.profilePicture && !user.profile_picture) {
      refreshUser?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  React.useEffect(() => {
    setAvatarError(false);
  }, [userProfilePic]);

  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const mediaRef = useRef(media);
  mediaRef.current = media;

  useEffect(() => {
    return () => {
      mediaRef.current.forEach((item) => {
        if (item?.previewUrl && item.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, []);

  const handleAddFiles = (filesList) => {
    const files = Array.from(filesList);
    if (files.length === 0) return;

    if (media.length + files.length > 4) {
      toast.error('Maximum 4 media attachments per post');
      return;
    }

    const validFiles = [];
    for (const file of files) {
      const validTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
        'video/mp4', 'video/webm', 'video/quicktime',
      ];
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type. Only images and videos are supported.`);
        continue;
      }
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} exceeds the 50MB file size limit.`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    const newMediaItems = validFiles.map((file) => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      type: file.type.startsWith('video') ? 'video' : 'image',
      name: file.name,
      size: file.size,
    }));

    setMedia((prev) => [...prev, ...newMediaItems]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = (e) => handleAddFiles(e.target.files);

  useEffect(() => {
    if (initialFiles?.length) handleAddFiles(initialFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    if (e.dataTransfer.files?.length > 0) handleAddFiles(e.dataTransfer.files);
  };

  const handlePaste = (e) => {
    if (e.clipboardData?.files?.length > 0) handleAddFiles(e.clipboardData.files);
  };

  const handleCameraCapture = (files, capturedCaption) => {
    if (capturedCaption && !content.trim()) setContent(capturedCaption);
    if (files?.length > 0) handleAddFiles(files);
  };

  const removeMedia = (index) => {
    setMedia((prev) => {
      const item = prev[index];
      if (item?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleGifSelect = (gif) => {
    if (media.length >= 4) return;
    setMedia((prev) => [
      ...prev,
      { id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, url: gif.url, previewUrl: gif.url, type: 'image' },
    ]);
    setShowGifPicker(false);
  };

  const handleAudioRecordComplete = ({ file, duration }) => {
    setShowAudioRecorder(false);
    setMedia((prev) => [
      ...prev,
      { id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, file, previewUrl: URL.createObjectURL(file), type: 'audio', duration },
    ]);
    toast.success('Voice note attached!');
  };

  const handleAddLocation = async () => {
    setLocationLoading(true);
    try {
      // Use Capacitor Geolocation plugin for native iOS/Android permission prompts
      const { Geolocation } = await import('@capacitor/geolocation');

      // Request permission first — this shows the native iOS dialog
      const perm = await Geolocation.requestPermissions();
      if (perm.location === 'denied') {
        toast.error('Location permission denied. Please enable it in Settings.');
        setLocationLoading(false);
        return;
      }

      const position = await Geolocation.getCurrentPosition({ timeout: 10000, enableHighAccuracy: true });
      const { latitude, longitude } = position.coords;
      setLocationLat(latitude);
      setLocationLon(longitude);
      try {
        const { data } = await axios.get(`/api/geocode/reverse?lat=${latitude}&lon=${longitude}`);
        setLocation(data.display_name || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
      } catch {
        setLocation('Current location');
      }
      setLocationLoading(false);
    } catch (err) {
      console.error('Geolocation error:', err);
      // Fallback: try browser geolocation with a hard timeout
      if (navigator.geolocation) {
        const timeoutId = setTimeout(() => {
          toast.error('Location request timed out');
          setLocationLoading(false);
        }, 10000);
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            clearTimeout(timeoutId);
            const { latitude, longitude } = position.coords;
            setLocationLat(latitude); setLocationLon(longitude);
            try {
              const { data } = await axios.get(`/api/geocode/reverse?lat=${latitude}&lon=${longitude}`);
              setLocation(data.display_name || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
            } catch { setLocation('Current location'); }
            finally { setLocationLoading(false); }
          },
          (geoErr) => {
            clearTimeout(timeoutId);
            console.error('Browser geolocation error:', geoErr);
            toast.error('Unable to retrieve location. Please check permissions.');
            setLocationLoading(false);
          },
          { timeout: 8000, enableHighAccuracy: true }
        );
      } else {
        toast.error('Location not available on this device');
        setLocationLoading(false);
      }
    }
  };

  const insertEmoji = (emojiData) => {
    const cursor = textareaRef.current ? textareaRef.current.selectionStart : content.length;
    setContent(content.slice(0, cursor) + emojiData.emoji + content.slice(cursor));
    setShowEmojiPicker(false);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const nextPos = cursor + emojiData.emoji.length;
        textareaRef.current.setSelectionRange(nextPos, nextPos);
      }
    }, 0);
  };

  const addPollOption = () => { if (pollOptions.length < 4) setPollOptions([...pollOptions, '']); };
  const removePollOption = (index) => { if (pollOptions.length > 2) setPollOptions(pollOptions.filter((_, i) => i !== index)); };
  const setPollOption = (index, value) => { const updated = [...pollOptions]; updated[index] = value; setPollOptions(updated); };

  const charCount = content.length;
  const charsRemaining = MAX_CHARS - charCount;
  const progressPercent = Math.min((charCount / MAX_CHARS) * 100, 100);

  const ringRadius = 10;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const strokeDashoffset = ringCircumference - (progressPercent / 100) * ringCircumference;
  const ringColor = charsRemaining < 0 ? '#ef4444' : charsRemaining <= 20 ? '#f59e0b' : '#6366f1';

  const hasValidPoll = showPoll && pollOptions.filter((o) => o.trim().length > 0).length >= 2;
  const canSubmit = (content.trim().length > 0 || media.length > 0 || hasValidPoll) && charCount <= MAX_CHARS;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading || uploading) return;
    setLoading(true);
    try {
      let finalMedia = [];
      const itemsToUpload = media.filter((m) => m.file);
      if (itemsToUpload.length > 0) {
        setUploading(true); setUploadProgress(10);
        const audioItems = itemsToUpload.filter((m) => m.type === 'audio');
        const visualItems = itemsToUpload.filter((m) => m.type !== 'audio');
        const uploadedVisualMedia = [];
        if (visualItems.length > 0) {
          const rawFiles = visualItems.map((m) => m.file);
          // Skip heavy compression — videos pass through as-is, images are compressed
          const compressedFiles = await compressMediaFiles(rawFiles, { type: 'post' });
          // Normalize video/webm files to .webm extension with clean mimetype (no codec strings)
          const normalizedFiles = compressedFiles.map((file) => {
            if (!file) return file;
            const baseType = file.type.split(';')[0].trim();
            if (baseType !== file.type) {
              // Strip codec params from type
              return new File([file], file.name, { type: baseType, lastModified: file.lastModified });
            }
            return file;
          });
          const formData = new FormData();
          formData.append('subfolder', 'posts');
          normalizedFiles.forEach((file) => formData.append('files', file));

          const response = await axios.post('/api/upload/multiple', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
            },
          });
          uploadedVisualMedia.push(...(response.data.media || []));
        }
        const uploadedAudioMedia = [];
        for (const audioItem of audioItems) {
          const formData = new FormData();
          formData.append('file', audioItem.file); formData.append('subfolder', 'audio');
          const res = await axios.post('/api/upload/single', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          if (res.data?.url) uploadedAudioMedia.push({ url: res.data.url, type: 'audio', duration: audioItem.duration });
        }
        let visualIdx = 0, audioIdx = 0;
        for (const item of media) {
          if (!item.file) finalMedia.push({ url: item.url, type: item.type || 'image' });
          else if (item.type === 'audio') { if (uploadedAudioMedia[audioIdx]) { finalMedia.push(uploadedAudioMedia[audioIdx]); audioIdx++; } }
          else { if (uploadedVisualMedia[visualIdx]) { finalMedia.push(uploadedVisualMedia[visualIdx]); visualIdx++; } }
        }
      } else {
        finalMedia = media.map((m) => ({ url: m.url, type: m.type || 'image' }));
      }

      const payload = {
        content: normalizeVideoUrlsInText(content.trim() || ''),
        media: finalMedia,
        whoCanReply,
        ...(location ? { location } : {}),
        ...(locationLat != null && locationLon != null ? { latitude: locationLat, longitude: locationLon } : {}),
        ...(hasValidPoll ? { poll: { options: pollOptions.map((o) => o.trim()).filter(Boolean), durationHours: pollDurationHours } } : {}),
        ...(communityId != null ? { communityId: Number(communityId) } : {}),
        ...(quotedPost ? { quotePostId: quotedPost.id } : {}),
      };

      await axios.post('/api/posts', payload);
      media.forEach((item) => { if (item.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(item.previewUrl); });
      setContent(''); setMedia([]); setLocation(''); setLocationLat(null); setLocationLon(null); setShowPoll(false); setPollOptions(['', '']);
      toast.success('Your post has been published!');
      if (onPostCreated) onPostCreated();
    } catch (error) {
      console.error('Create post error:', error);
      toast.error(error.response?.data?.error || 'Failed to publish post');
    } finally {
      setLoading(false); setUploading(false); setUploadProgress(0);
    }
  };

  const currentReplyOption = REPLY_OPTIONS.find((o) => o.value === whoCanReply);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative flex flex-col ${isDragging ? 'ring-2 ring-indigo-500 rounded-xl bg-indigo-50/30 dark:bg-indigo-950/20' : ''}`}
    >
      {/* ── Sticky Header (Post button lives here) ── */}
      {showHeader && (
        // Full-screen compose has no app top bar above it, so clear the status bar here
        <div className="sticky top-0 z-20 flex items-center justify-between px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] bg-white/95 dark:bg-black/95 backdrop-blur-md border-b border-zinc-100 dark:border-zinc-800/80">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center justify-center w-9 h-9 -ml-1 rounded-full text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            aria-label={t('common.back')}
          >
            <i className="fas fa-arrow-left text-lg" />
          </button>

          {/* Post / Publish button */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || loading || uploading}
            className={`px-5 py-1.5 rounded-full text-sm font-bold transition-all duration-200 ${
              canSubmit && !loading && !uploading
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-500/30 active:scale-95'
                : 'text-zinc-400 dark:text-zinc-600 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                {t('common.loading')}
              </span>
            ) : t('common.post', 'Post')}
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col flex-1">
        {/* ── Composer body ── */}
        <div
          className={`flex gap-3 px-4 pt-4 ${showHeader ? '' : 'border-b border-zinc-100 dark:border-zinc-800/80 pb-3'}`}
        >
          {/* Avatar */}
          <Link
            to={user?.handle ? `/profile/${user.handle.replace(/^@+/, '')}` : '#'}
            className="flex-shrink-0 pt-0.5 group/avatar transition-transform hover:scale-105 active:scale-95"
            title={user?.username ? `View ${user.username}'s profile` : 'View profile'}
          >
            {userProfilePic && !avatarError ? (
              <img
                src={getMediaUrl(userProfilePic)}
                alt={user?.username || 'You'}
                onError={() => setAvatarError(true)}
                className="w-10 h-10 rounded-full object-cover border border-zinc-200 dark:border-zinc-800 group-hover/avatar:ring-2 group-hover/avatar:ring-indigo-500/50 transition-all"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center font-bold text-white text-sm group-hover/avatar:ring-2 group-hover/avatar:ring-indigo-500/50 transition-all">
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
            )}
          </Link>

          {/* Right column: name + textarea */}
          <div className="flex-1 min-w-0">
            {/* Username + privacy toggle inline */}
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-sm font-bold text-zinc-900 dark:text-zinc-100 leading-none">
                {user?.username || 'You'}
              </span>
              {/* Compact privacy pill — under the name */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowReplyDropdown(!showReplyDropdown)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors"
                >
                  <i className={`fas ${currentReplyOption?.icon || 'fa-globe'} text-[10px]`} />
                  <span>{t(currentReplyOption?.labelKey ?? 'compose.replyEveryone')}</span>
                  <i className="fas fa-chevron-down text-[9px]" />
                </button>
                {showReplyDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowReplyDropdown(false)} aria-hidden="true" />
                    <div className="absolute left-0 top-full mt-1.5 py-1.5 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-xl z-20 min-w-[200px]">
                      {REPLY_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => { setWhoCanReply(opt.value); setShowReplyDropdown(false); }}
                          className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-xs font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-800 ${
                            whoCanReply === opt.value ? 'text-indigo-600 dark:text-indigo-400' : 'text-zinc-700 dark:text-zinc-300'
                          }`}
                        >
                          <i className={`fas ${opt.icon} w-4 text-center`} />
                          {t(opt.labelKey)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Textarea — no divider, full breathing room */}
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onPaste={handlePaste}
              placeholder={t('compose.placeholder', "What's happening?")}
              className="w-full border-none resize-none focus:outline-none text-[17px] text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 font-normal bg-transparent leading-relaxed mt-1.5"
              rows={media.length > 0 || showPoll ? 2 : 4}
              autoFocus={showHeader}
            />

            {/* Mention suggestions */}
            <MentionSuggestions textareaRef={textareaRef} content={content} setContent={setContent} />

            {/* Drag overlay */}
            {isDragging && (
              <div className="p-3 my-2 text-center text-sm font-semibold text-indigo-600 dark:text-indigo-400 border border-dashed border-indigo-400 rounded-xl bg-indigo-50/60 dark:bg-indigo-950/40">
                Drop your photos or videos here to attach
              </div>
            )}

            {/* Attached location chip */}
            {location && (
              <div className="mt-1 mb-2 inline-flex items-center gap-2 px-3 py-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded-full text-xs font-medium">
                <i className="fas fa-map-marker-alt text-rose-500" />
                <span>{location}</span>
                <button type="button" onClick={() => { setLocation(''); setLocationLat(null); setLocationLon(null); }} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 ml-1">
                  <i className="fas fa-times" />
                </button>
              </div>
            )}

            {/* Poll */}
            {showPoll && (
              <div className="mt-2 mb-3 p-3.5 border border-zinc-200 dark:border-zinc-800 rounded-2xl bg-zinc-50/60 dark:bg-zinc-900/40 space-y-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Poll Options</span>
                  <button type="button" onClick={() => setShowPoll(false)} className="text-xs text-rose-500 hover:underline font-semibold">Remove</button>
                </div>
                {pollOptions.map((opt, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => setPollOption(i, e.target.value)}
                      placeholder={`Choice ${i + 1}`}
                      className="flex-1 border border-zinc-200 dark:border-zinc-700 rounded-xl px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100"
                      maxLength={100}
                    />
                    {pollOptions.length > 2 && (
                      <button type="button" onClick={() => removePollOption(i)} className="text-zinc-400 hover:text-rose-500 px-2">
                        <i className="fas fa-trash-alt text-xs" />
                      </button>
                    )}
                  </div>
                ))}
                {pollOptions.length < 4 && (
                  <button type="button" onClick={addPollOption} className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline">+ Add choice</button>
                )}
                <div className="pt-2 flex items-center gap-2">
                  <label className="text-xs text-zinc-500">Duration:</label>
                  <select value={pollDurationHours} onChange={(e) => setPollDurationHours(Number(e.target.value))} className="border border-zinc-200 dark:border-zinc-700 rounded-lg px-2 py-1 text-xs bg-white dark:bg-zinc-900 text-zinc-800 dark:text-zinc-200">
                    {POLL_DURATION_KEYS.map((d) => (<option key={d.value} value={d.value}>{t(d.labelKey)}</option>))}
                  </select>
                </div>
              </div>
            )}

            {/* Audio Recorder */}
            {showAudioRecorder && (
              <AudioRecorder onRecordComplete={handleAudioRecordComplete} onCancel={() => setShowAudioRecorder(false)} />
            )}

            {/* Media Grid */}
            {media.length > 0 && (
              <div className={`mt-2 mb-3 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800 ${media.length === 1 ? '' : 'grid grid-cols-2 gap-1.5 p-1'}`}>
                {media.map((item, index) => {
                  const mediaSrc = item.previewUrl || (item.url ? getMediaUrl(item.url) : '');
                  return (
                    <div key={item.id || index} className="relative group overflow-hidden rounded-xl">
                      {item.type === 'audio' ? (
                        <div className="p-2 w-full"><AudioPlayer src={mediaSrc} duration={item.duration} /></div>
                      ) : item.type === 'video' ? (
                        <video src={mediaSrc} className={`w-full ${media.length === 1 ? 'max-h-96 object-contain bg-black' : 'h-44 object-cover'}`} controls />
                      ) : (
                        <img src={mediaSrc} alt={`Attachment ${index + 1}`} className={`w-full ${media.length === 1 ? 'max-h-96 object-contain bg-zinc-100 dark:bg-zinc-900' : 'h-44 object-cover'}`} />
                      )}
                      {item.type === 'image' && !item.url?.includes('giphy.com') && (
                        <button type="button" onClick={() => setEditingImageIndex(index)} className="absolute top-2 left-2 px-2.5 py-1 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center gap-1.5 text-xs font-semibold backdrop-blur-md transition-all shadow-md group-hover:scale-105 z-20" title={t('imageEditor.title', 'Edit Image')}>
                          <i className="fas fa-magic text-xs text-indigo-400" />
                          <span>{t('common.edit', 'Edit')}</span>
                        </button>
                      )}
                      <button type="button" onClick={() => removeMedia(index)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center transition-all shadow-md group-hover:scale-105 z-20" aria-label="Remove media">
                        <i className="fas fa-times text-xs" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Upload progress */}
            {uploading && (
              <div className="w-full bg-zinc-100 dark:bg-zinc-800 h-1.5 rounded-full overflow-hidden mb-2">
                <div className="bg-indigo-500 h-full transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
              </div>
            )}

            {/* Quoted post preview */}
            {quotedPost && (
              <div className="mt-1 mb-3 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  {quotedPost.author?.profilePicture ? (
                    <img src={quotedPost.author.profilePicture} alt={quotedPost.author?.username || 'User'} className="w-5 h-5 rounded-full object-cover border border-zinc-200 dark:border-zinc-700 shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-zinc-600 dark:text-zinc-300 shrink-0">{(quotedPost.author?.username || 'U').charAt(0).toUpperCase()}</div>
                  )}
                  <span className="font-bold text-zinc-900 dark:text-zinc-100 text-[13.5px] truncate"><bdi>{quotedPost.author?.username || 'User'}</bdi></span>
                  {(quotedPost.author?.handle || quotedPost.author?.username) && (
                    <span className="text-zinc-500 dark:text-zinc-400 text-xs truncate"><bdi dir="ltr">@{(quotedPost.author?.handle || quotedPost.author?.username || '').replace(/^@+/, '')}</bdi></span>
                  )}
                </div>
                {quotedPost.content && (
                  <p className="text-zinc-700 dark:text-zinc-300 text-[14px] leading-snug break-words whitespace-pre-wrap">
                    {quotedPost.content.slice(0, 200)}{quotedPost.content.length > 200 ? '…' : ''}
                  </p>
                )}
              </div>
            )}

            {/* AI Studio Panel */}
            {showAIStudio && (
              <BazzAIStudio content={content} onUpdate={(newContent) => setContent(newContent)} onClose={() => setShowAIStudio(false)} />
            )}
          </div>
        </div>

        {/* ── Keyboard-Docking Toolbar ── */}
        <div className="sticky bottom-0 z-10 bg-white/95 dark:bg-black/95 backdrop-blur-md border-t border-zinc-100 dark:border-zinc-800/80 px-3 py-2 safe-area-bottom">
          <div className="flex items-center justify-between max-w-sm mx-auto">

            {/* Left: media action icons (brand indigo) */}
            <div className="flex items-center gap-0.5 text-indigo-600 dark:text-indigo-400">

              {/* Media (Gallery + Videos combined) */}
              <label
                className="w-9 h-9 rounded-full hover:bg-indigo-50 dark:hover:bg-indigo-950/40 flex items-center justify-center cursor-pointer transition-colors"
                title={t('compose.addMedia', 'Photos & Videos')}
              >
                <i className="fas fa-image text-[17px]" />
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={uploading || media.length >= 4}
                />
              </label>

              {/* In-app Camera */}
              <button
                type="button"
                onClick={() => setShowCamera(true)}
                disabled={uploading || media.length >= 4}
                className="w-9 h-9 rounded-full hover:bg-indigo-50 dark:hover:bg-indigo-950/40 flex items-center justify-center transition-colors disabled:opacity-40"
                title={t('compose.camera', 'Take photo/video')}
              >
                <i className="fas fa-camera text-[17px]" />
              </button>

              {/* GIF */}
              <button
                type="button"
                onClick={() => setShowGifPicker(true)}
                disabled={media.length >= 4}
                className="w-9 h-9 rounded-full hover:bg-indigo-50 dark:hover:bg-indigo-950/40 flex items-center justify-center transition-colors disabled:opacity-40"
                title="GIF"
              >
                <i className="fas fa-photo-film text-[17px]" />
              </button>

              {/* Poll */}
              <button
                type="button"
                onClick={() => setShowPoll(!showPoll)}
                disabled={media.length > 0}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 ${
                  showPoll ? 'bg-indigo-100 dark:bg-indigo-950/60 text-indigo-600' : 'hover:bg-indigo-50 dark:hover:bg-indigo-950/40'
                }`}
                title="Poll"
              >
                <i className="fas fa-chart-bar text-[17px]" />
              </button>

              {/* Voice note */}
              <button
                type="button"
                onClick={() => setShowAudioRecorder(!showAudioRecorder)}
                disabled={media.length >= 4 || showPoll}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 ${
                  showAudioRecorder ? 'bg-rose-50 text-rose-500 dark:bg-rose-950/40' : 'hover:bg-indigo-50 dark:hover:bg-indigo-950/40'
                }`}
                title="Voice Note"
              >
                <i className="fas fa-microphone text-[17px]" />
              </button>

              {/* Location */}
              <button
                type="button"
                onClick={handleAddLocation}
                disabled={!!location || locationLoading}
                className="w-9 h-9 rounded-full hover:bg-indigo-50 dark:hover:bg-indigo-950/40 flex items-center justify-center transition-colors disabled:opacity-40"
                title="Location"
              >
                {locationLoading ? (
                  <span className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <i className="fas fa-map-marker-alt text-[17px]" />
                )}
              </button>

              {/* AI Studio */}
              <button
                type="button"
                onClick={() => setShowAIStudio((prev) => !prev)}
                disabled={content.trim().length === 0}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 ${
                  showAIStudio ? 'bg-violet-100 text-violet-600 dark:bg-violet-950/40 dark:text-violet-400' : 'hover:bg-indigo-50 dark:hover:bg-indigo-950/40'
                }`}
                title={t('ai.studioTitle', 'AI Creator Studio')}
              >
                ✨
              </button>
            </div>

            {/* Right: char counter ring + Post button (only when NOT in header mode) */}
            <div className="flex items-center gap-3">
              {charCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <svg className="w-7 h-7 -rotate-90">
                    <circle cx="14" cy="14" r={ringRadius} stroke="#e5e7eb" className="dark:stroke-zinc-800" strokeWidth="2.5" fill="none" />
                    <circle cx="14" cy="14" r={ringRadius} stroke={ringColor} strokeWidth="2.5" strokeDasharray={ringCircumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" fill="none" className="transition-all duration-150" />
                  </svg>
                  {charsRemaining <= 20 && (
                    <span className={`text-xs font-bold ${charsRemaining < 0 ? 'text-rose-500' : 'text-amber-500'}`}>{charsRemaining}</span>
                  )}
                </div>
              )}

              {/* Emoji picker (collapsed here to save space, still accessible) */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker((prev) => !prev)}
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors text-indigo-600 dark:text-indigo-400 ${
                    showEmojiPicker ? 'bg-indigo-100 dark:bg-indigo-950/60' : 'hover:bg-indigo-50 dark:hover:bg-indigo-950/40'
                  }`}
                  title="Emoji"
                >
                  <i className="far fa-smile text-[17px]" />
                </button>
                {showEmojiPicker && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowEmojiPicker(false)} aria-hidden="true" />
                    <div className="absolute bottom-full right-0 mb-2 z-50 shadow-2xl rounded-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150 border border-zinc-200/80 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                      <EmojiPicker
                        onEmojiClick={insertEmoji}
                        width={isMobile ? Math.min(280, typeof window !== 'undefined' ? window.innerWidth - 32 : 280) : 300}
                        height={isMobile ? 260 : 300}
                        theme={typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                        searchPlaceHolder="Search emoji..."
                        previewConfig={{ showPreview: false }}
                        skinTonesDisabled={true}
                        lazyLoadEmojis={true}
                        style={{
                          '--epr-emoji-size': '22px', '--epr-emoji-padding': '4px', '--epr-header-padding': '6px 8px',
                          '--epr-search-input-height': '32px', '--epr-category-navigation-button-size': '22px',
                          '--epr-category-label-height': '22px', '--epr-picker-border-radius': '16px',
                        }}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Post button — only shown when NOT using header mode */}
              {!showHeader && (
                <button
                  type="submit"
                  disabled={!canSubmit || loading || uploading}
                  className={`px-5 py-1.5 rounded-full text-sm font-bold transition-all duration-200 ${
                    canSubmit && !loading && !uploading
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-500/20 active:scale-95'
                      : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-600 cursor-not-allowed'
                  }`}
                >
                  {loading ? t('common.loading') : t('common.post', 'Post')}
                </button>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* GIF Picker */}
      {showGifPicker && <GifPicker onSelect={handleGifSelect} onClose={() => setShowGifPicker(false)} />}

      {/* Image Editor Modal */}
      {editingImageIndex !== null && media[editingImageIndex] && (
        <ImageEditorModal
          isOpen={editingImageIndex !== null}
          imageUrl={media[editingImageIndex].previewUrl || media[editingImageIndex].url}
          onClose={() => setEditingImageIndex(null)}
          onSave={(newMedia) => {
            setMedia((prev) => {
              const copy = [...prev];
              const prevItem = copy[editingImageIndex];
              if (prevItem?.previewUrl?.startsWith('blob:') && prevItem.previewUrl !== newMedia.previewUrl) URL.revokeObjectURL(prevItem.previewUrl);
              copy[editingImageIndex] = { ...prevItem, id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, file: newMedia.file, previewUrl: newMedia.previewUrl, url: newMedia.previewUrl, type: 'image', name: newMedia.file?.name || prevItem?.name || 'edited_image.jpg', size: newMedia.size || newMedia.file?.size };
              return copy;
            });
            setEditingImageIndex(null);
          }}
          subfolder="posts"
          uploadOnSave={false}
        />
      )}

      {/* Camera Modal */}
      <CameraModal
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleCameraCapture}
        maxPhotos={Math.max(1, 4 - media.length)}
        initialCaption={content}
      />
    </div>
  );
};

export default CreatePost;
